import Vue from "vue";
import axios from "axios";

// Situação dos módulos do site configurada no backoffice (Site > Configurações do portal > Módulos e prazos).
// Enquanto a configuração não carrega (ou se a API falhar), tudo fica liberado, como antes desta tela existir;
// o backend continua bloqueando as ações fechadas.
const CACHE_MS = 60 * 1000;

// Datas usadas nos avisos do site (Módulos e prazos > Parâmetros). Os padrões são os valores atuais,
// usados enquanto a API não responde.
const DEFAULT_PARAMETERS: Record<string, string> = {
  "review.timely_cutoff": "2026-01-31 23:59:59",
  "registration.credentialing_deadline": "2025-12-10 23:59:59",
  "review.lawyer_notice_after": "2026-02-06",
};

const state = Vue.observable({ loaded: false, items: {} as Record<string, any>, parameters: { ...DEFAULT_PARAMETERS } });
let loadedAt = 0;
let pending: Promise<void> | null = null;

function load(force = false): Promise<void> {
  if (!force && state.loaded && Date.now() - loadedAt < CACHE_MS) return Promise.resolve();
  if (pending) return pending;
  pending = Promise.all([
    axios.get("/api/module-settings/site")
      .then((response) => {
        const items = (response.data && response.data.content) || [];
        state.items = items.reduce((result: Record<string, any>, item: any) => { result[item.key] = item; return result; }, {});
        state.loaded = true;
        loadedAt = Date.now();
      })
      .catch(() => { /* mantém o último estado conhecido */ }),
    axios.get("/api/module-settings/site/parameters")
      .then((response) => {
        const values = (response.data && response.data.content) || {};
        state.parameters = { ...DEFAULT_PARAMETERS, ...values };
      })
      .catch(() => { /* mantém os padrões */ }),
  ])
    .then(() => undefined)
    .finally(() => { pending = null; });
  return pending;
}

const modules = {
  state,
  load,
  // Ação disponível agora.
  isOpen(key: string): boolean {
    const item = state.items[key];
    return !item || item.open;
  },
  // Aparece no menu e nas telas (aberto, ou fechado em modo "somente leitura").
  isVisible(key: string): boolean {
    const item = state.items[key];
    return !item || item.open || item.visibility !== "hidden";
  },
  // Mensagem configurada para quando o módulo está fechado.
  message(key: string): string {
    const item = state.items[key];
    return item && !item.open ? item.message || "Este módulo está fechado no momento." : "";
  },
  // Valor de um parâmetro de data (ex.: parameter("review.timely_cutoff") => "2026-01-31 23:59:59").
  parameter(key: string): string {
    return state.parameters[key] || DEFAULT_PARAMETERS[key] || "";
  },
  // Data do parâmetro formatada para os textos (DD/MM/AAAA).
  parameterDate(key: string): string {
    const [date] = this.parameter(key).split(" ");
    const [year, month, day] = date.split("-");
    return day && month && year ? `${day}/${month}/${year}` : "";
  },
  // Hora do parâmetro para os textos (ex.: "23h59").
  parameterTime(key: string): string {
    const [, time] = this.parameter(key).split(" ");
    if (!time) return "";
    const [hours, minutes] = time.split(":");
    return `${hours}h${minutes}`;
  },
  deadline(key: string): { value: number; unit: string } | null {
    const item = state.items[key];
    return item ? item.deadline : null;
  },
};

Vue.prototype.$modules = modules;

// Use no início de uma ação: se o módulo estiver fechado, avisa com a mensagem configurada e retorna true.
Vue.prototype.$moduleBlocked = function (key: string): boolean {
  if (modules.isOpen(key)) return false;
  if (this.$dialog && this.$dialog.notify) this.$dialog.notify.info(modules.message(key), { position: "top-right", timeout: 6000 });
  return true;
};

export default modules;
